import { useEffect, useState } from "react";
import { api, errMsg, formatRupiah } from "@/lib/api";
import { Plus, X } from "lucide-react";

const TABS = [
  { k: "clients", l: "Klien", fields: [["name","Nama"],["type","Tipe","select",["Perorangan","Perusahaan"]],["phone","Telepon"],["email","Email"],["segment","Segmen","select",["Wedding","Corporate","Brand Activation","Expo"]],["address","Alamat","textarea"]] },
  { k: "venues", l: "Venue", fields: [["name","Nama"],["city","Kota"],["contact","Kontak"],["vendor_fee","Vendor Fee","number"],["deposit","Deposit","number"],["rating","Rating","number"],["lat","Latitude (opsional)","number"],["lng","Longitude (opsional)","number"],["radius_m","Radius absen, meter (opsional)","number"]] },
  { k: "vendors", l: "Vendor", fields: [["name","Nama"],["specialty","Spesialisasi"],["phone","Telepon"],["standard_rate","Tarif Standar","number"],["rating","Rating","number"]] },
  { k: "partners", l: "Mitra", fields: [["name","Nama"],["category","Kategori","select",["WO","Catering","Venue","Lainnya"]],["phone","Telepon"],["commission_scheme","Skema Komisi"]] },
  { k: "packages", l: "Paket", fields: [["name","Nama"],["version","Versi"],["base_price","Harga Dasar","number"],["segment","Segmen","select",["Wedding","Corporate"]]] },
];

export default function Masters() {
  const [tab, setTab] = useState("clients");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const current = TABS.find(t => t.k === tab);

  async function load() {
    const { data } = await api.get(`/${tab}`);
    setList(data);
  }
  useEffect(() => { load(); setForm({}); }, [tab]);

  async function setLocation(v) {
    const cur = v.lat ? `${v.lat}, ${v.lng}` : "";
    const val = window.prompt("Tempel koordinat venue dari Google Maps (lat, lng):", cur);
    if (!val) return;
    const [lat, lng] = val.split(",").map(x => Number(x.trim()));
    if (isNaN(lat) || isNaN(lng)) { setErr("Format koordinat salah. Contoh: -6.2183, 106.8022"); return; }
    const r = window.prompt("Radius absen (meter). Kosongkan untuk default:", v.radius_m || "");
    try {
      await api.patch(`/venues/${v.id}/location`, { lat, lng, radius_m: r ? Number(r) : null });
      setErr(""); await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  async function create(e) {
    e.preventDefault();
    try {
      const payload = { ...form };
      current.fields.forEach(([k,,t]) => { if (t === "number") payload[k] = payload[k] === undefined || payload[k] === "" ? (["lat","lng","radius_m"].includes(k) ? null : 0) : Number(payload[k]); });
      await api.post(`/${tab}`, payload);
      setOpen(false); setForm({}); await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  return (
    <div className="p-4 sm:p-8 space-y-5">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Master Data</h1>
          <p className="text-sm text-[#536263]">Klien, venue, vendor, mitra, paket</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="master-add-btn"><Plus size={16} />Tambah</button>
      </header>

      <div className="flex gap-2 overflow-x-auto no-scrollbar">
        {TABS.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`master-tab-${t.k}`}
            className={`chip whitespace-nowrap ${tab === t.k ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>{t.l}</button>
        ))}
      </div>

      {err && !open && <div className="text-sm text-red-700" data-testid="master-error">{err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`master-list-${tab}`}>
        {list.map(x => (
          <div key={x.id} className="card-editorial p-4">
            <h3 className="font-serif-display text-lg font-semibold">{x.name}</h3>
            <div className="text-sm text-[#536263] mt-1">
              {x.city && <div>Kota: {x.city}</div>}
              {x.specialty && <div>Spesialisasi: {x.specialty}</div>}
              {x.segment && <div>Segmen: {x.segment}</div>}
              {x.phone && <div>Telp: {x.phone}</div>}
              {x.base_price != null && <div className="font-mono-num text-[#0F5257]">{formatRupiah(x.base_price)}</div>}
            </div>
            {tab === "venues" && (
              <div className="mt-3 flex items-center justify-between gap-2">
                <span className={`text-xs ${x.lat ? "text-[#2D6A4F]" : "text-amber-700"}`} data-testid={`venue-location-${x.id}`}>
                  {x.lat ? `Lokasi: ${x.lat}, ${x.lng}${x.radius_m ? ` · ${x.radius_m} m` : ""}` : "Lokasi belum diatur"}
                </span>
                <button onClick={() => setLocation(x)} className="chip bg-[#F7EFE8] text-[#B87333]" data-testid={`venue-set-location-${x.id}`}>Set Lokasi</button>
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full text-center p-12 text-[#8A999A]">Kosong</div>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)}>
          <form onSubmit={create} onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md p-5 space-y-3 max-h-[90vh] overflow-y-auto" data-testid="master-form">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl font-bold">Tambah {current.l}</h3>
              <button type="button" onClick={() => setOpen(false)}><X /></button>
            </div>
            {current.fields.map(([k, label, type, opts]) => (
              type === "select" ? (
                <select key={k} value={form[k] || ""} onChange={e => setForm({...form, [k]: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11">
                  <option value="">{label}</option>
                  {opts.map(o => <option key={o}>{o}</option>)}
                </select>
              ) : type === "textarea" ? (
                <textarea key={k} placeholder={label} value={form[k] || ""} onChange={e => setForm({...form, [k]: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 py-2" />
              ) : (
                <input key={k} type={type || "text"} placeholder={label} value={form[k] || ""} onChange={e => setForm({...form, [k]: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />
              )
            ))}
            {err && <div className="text-sm text-red-700">{err}</div>}
            <button type="submit" className="btn-primary w-full" data-testid="master-submit">Simpan</button>
          </form>
        </div>
      )}
    </div>
  );
}
